import {Environment, getVersionName} from '@aboutyou/config';
import {DD_ENV_NAME_MAP} from '../constants';
import {
  DATADOG_SERVER_LOGS_API_KEY,
  postToDatadogLogsIntake,
} from './datadogLogsIntake';

// The Datadog pipeline for `ddsource: nodejs` derives the official log status
// (the red/orange severity in the Logs UI) from the numeric pino-style
// `level` attribute. A `status` string alone is kept as a custom attribute
// and the log stays `info`. 30/40/50 are the pino levels for info/warn/error.
const PINO_LEVEL_BY_STATUS = {
  info: 30,
  warn: 40,
  error: 50,
} as const;

export type ServerDatadogLog = {
  message: string;
  hostname: string;
  environment: Environment;
  status?: 'info' | 'error' | 'warn';
  ddsource?: string;
  attributes?: Record<string, unknown>;
};

export const sendServerLogToDatadog = (log: ServerDatadogLog): void => {
  if (
    process.browser ||
    process.env.NODE_ENV === 'test' ||
    !DATADOG_SERVER_LOGS_API_KEY
  ) {
    return;
  }

  const service = `aysa-frontend-${process.env.STACK}`;
  const environment = log.environment;
  const env = environment ? DD_ENV_NAME_MAP[environment] : 'unknown';
  const version = getVersionName();

  const ddtags = [
    `env:${env}`,
    `version:${version}`,
    'monitored-by:datadoghq.eu',
    'tenant-short-key:ayou',
    `service:${service}`,
  ].join(',');

  const status = log.status ?? 'info';

  const payload = {
    level: PINO_LEVEL_BY_STATUS[status],
    ddsource: log.ddsource ?? 'nodejs',
    service,
    env,
    status,
    message: log.message,
    ddtags,
    hostname: log.hostname,
    ...log.attributes,
    ...(log.attributes?.error
      ? {
          error_message:
            typeof log.attributes.error === 'string'
              ? log.attributes.error
              : JSON.stringify(log.attributes.error),
          detail: log.attributes.details,
        }
      : {}),
  };

  // Fire-and-forget: not awaited so callers never block on log delivery
  postToDatadogLogsIntake(payload).catch(err => {
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.error('Failed to send server log to Datadog:', err);
    }
  });
};
